import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle,
  TrendingDown, 
  Clock, 
  DollarSign,
  Calculator,
  Target,
  AlertCircle,
  TrendingUp,
  Calendar,
  Users,
  Building,
  Briefcase,
  Code,
  Stethoscope,
  Home
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import NavBar from '../../components/NavBar';
import ResponsiveFooter from '../../components/ResponsiveFooter';

// EXACT color palette from WorkflowReplacementDemo.jsx - only blue, black, grey
const colors = {
  // Primary accent - deeper blue for a more formal, enterprise feel
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  
  // Neutral tones
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray600: '#9e9e9e',
  gray700: '#636363',
  gray900: '#333333',
};

// EXACT font styles from WorkflowReplacementDemo.jsx
const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600, // Semi-bold for headings
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400, // Regular for body text
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500, // Medium weight
  },
  light: {
    fontWeight: 300, // Light weight
  }
};

// Shimmer animation for CTAs
const shimmerVariants = {
  initial: { x: "-100%" },
  animate: { 
    x: "100%",
    transition: {
      duration: 2,
      ease: "easeInOut",
      repeat: Infinity,
      repeatDelay: 3
    }
  }
};

// Industry presets with realistic data
const industryPresets = {
  tech: {
    name: 'Tech & Startups',
    icon: Code,
    avgContracts: 15,
    avgLawyerRate: 450,
    teamSize: 4,
    commonContracts: ['NDAs', 'Vendor Agreements', 'Employment Contracts', 'IP Licenses']
  },
  realestate: {
    name: 'Real Estate',
    icon: Building,
    avgContracts: 25,
    avgLawyerRate: 350,
    teamSize: 5,
    commonContracts: ['Purchase Agreements', 'Leases', 'Property Management', 'Closing Docs']
  },
  consulting: {
    name: 'Consulting',
    icon: Briefcase,
    avgContracts: 12,
    avgLawyerRate: 400,
    teamSize: 3,
    commonContracts: ['SOWs', 'Master Agreements', 'Consulting Contracts', 'Amendments']
  },
  healthcare: {
    name: 'Healthcare',
    icon: Stethoscope,
    avgContracts: 30,
    avgLawyerRate: 375,
    teamSize: 6,
    commonContracts: ['Provider Agreements', 'HIPAA Contracts', 'Vendor Agreements', 'Employment']
  },
  residential: {
    name: 'Residential Services',
    icon: Home,
    avgContracts: 10,
    avgLawyerRate: 300,
    teamSize: 2,
    commonContracts: ['Service Agreements', 'Maintenance Contracts', 'Vendor Agreements', 'Employment']
  }
};

const ContractWasteCalculator = () => {
  const [selectedIndustry, setSelectedIndustry] = useState('tech');
  const [contractsPerMonth, setContractsPerMonth] = useState(industryPresets.tech.avgContracts);
  const [lawyerRate, setLawyerRate] = useState(industryPresets.tech.avgLawyerRate);
  const [teamSize, setTeamSize] = useState(industryPresets.tech.teamSize);

  // Animation refs
  const [calculatorRef, calculatorInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update inputs when industry changes
  const handleIndustryChange = (industry) => {
    setSelectedIndustry(industry);
    setContractsPerMonth(industryPresets[industry].avgContracts);
    setLawyerRate(industryPresets[industry].avgLawyerRate);
    setTeamSize(industryPresets[industry].teamSize);
  };

  // Calculate the waste (based on verified industry research)
  const calculateWaste = () => {
    // Industry-verified breakdown per contract
    const hoursPerContract = 26; // Industry standard from research
    const costPerContract = 3700; // Verified industry average
    
    // Current monthly waste
    const monthlyContracts = contractsPerMonth;
    const monthlyHours = monthlyContracts * hoursPerContract;
    const monthlyCost = monthlyContracts * costPerContract;
    
    // Annual projections
    const annualContracts = monthlyContracts * 12;
    const annualHours = monthlyHours * 12;
    const annualCost = monthlyCost * 12;
    
    // Lost revenue opportunity (based on team productivity)
    const hourlyValue = teamSize * 75; // Conservative $75/hour blended rate
    const lostProductivity = annualHours * hourlyValue;
    
    // Contract value erosion (8.6% industry average per Deloitte)
    const estimatedContractValue = annualContracts * 50000; // Conservative $50k average
    const valueErosion = estimatedContractValue * 0.086;
    
    // Total annual waste
    const totalAnnualWaste = annualCost + lostProductivity + valueErosion;
    
    // Industry benchmarks for context
    const industryData = industryPresets[selectedIndustry];
    
    return {
      monthlyContracts,
      monthlyHours,
      monthlyCost,
      annualContracts,
      annualHours,
      annualCost,
      lostProductivity,
      valueErosion,
      totalAnnualWaste,
      industryData,
      costPerContract,
      hoursPerContract
    };
  };

  const wasteData = calculateWaste();

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col overflow-hidden" 
         style={{
           color: colors.gray900,
           fontFamily: fontStyles.fontFamily
         }}>
      
      <NavBar activePage="pricing" />
      
      {/* Hero Section */}
      <motion.div 
        className="py-32"
        ref={calculatorRef}
        style={{backgroundColor: colors.gray50}}
      >
        <div className="container mx-auto px-6 max-w-7xl">
          <motion.div 
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            animate={calculatorInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4" 
                 style={{
                   backgroundColor: colors.accentLight, 
                   color: colors.accent
                 }}>
              Contract Waste Calculator
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-8" style={{letterSpacing: '-0.02em', ...fontStyles.heading}}>
              How much is your <span style={{color: colors.accent}}>contract chaos</span> costing you?
            </h1>
            <p className="text-xl max-w-4xl mx-auto" style={{color: colors.gray700, lineHeight: '1.6', ...fontStyles.body}}>
              Based on industry research: Companies lose 9.2% of their bottom line to poor contract management. <br/>
              Calculate your exact annual waste below.
            </p>
          </motion.div>

          {/* Industry Selection */}
          <motion.div
            className="flex flex-wrap justify-center gap-4 mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={calculatorInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {Object.entries(industryPresets).map(([key, industry]) => (
              <motion.button
                key={key}
                onClick={() => handleIndustryChange(key)}
                className="px-6 py-3 rounded-2xl font-medium transition-all flex items-center"
                style={{
                  backgroundColor: selectedIndustry === key ? colors.accent : colors.white,
                  color: selectedIndustry === key ? colors.white : colors.gray700,
                  border: `1px solid ${selectedIndustry === key ? colors.accent : colors.gray200}`,
                  boxShadow: selectedIndustry === key ? `0 8px 32px rgba(30, 64, 175, 0.3)` : '0 4px 16px rgba(0, 0, 0, 0.04)',
                  ...fontStyles.medium
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <industry.icon size={18} className="mr-2" />
                {industry.name}
              </motion.button>
            ))}
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Input Controls */}
            <motion.div 
              className="space-y-8"
              initial={{ opacity: 0, x: -30 }}
              animate={calculatorInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="p-8 rounded-3xl border"
                   style={{
                     borderColor: colors.gray200,
                     background: `linear-gradient(135deg, ${colors.white} 0%, ${colors.gray50} 100%)`,
                     boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)'
                   }}>
                <div className="flex items-center mb-8">
                  <Calculator size={24} style={{color: colors.accent}} className="mr-3" />
                  <h3 className="text-2xl font-bold" style={fontStyles.heading}>Your Current Situation</h3>
                </div>
                
                {[
                  { 
                    label: "Contracts processed monthly", 
                    value: contractsPerMonth, 
                    setter: setContractsPerMonth, 
                    min: 5, 
                    max: 50,
                    helpText: `Typical for ${wasteData.industryData.name}: ${wasteData.industryData.avgContracts}/month`
                  },
                  { 
                    label: "Lawyer hourly rate", 
                    value: lawyerRate, 
                    setter: setLawyerRate, 
                    min: 200, 
                    max: 600, 
                    prefix: "$",
                    helpText: "Industry range: $200-600/hour for contract review"
                  },
                  { 
                    label: "Team members handling contracts", 
                    value: teamSize, 
                    setter: setTeamSize, 
                    min: 1, 
                    max: 15,
                    helpText: "People whose time is consumed by contract workflows"
                  }
                ].map((control, index) => (
                  <div key={control.label} className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                      <label className="text-lg font-semibold" style={fontStyles.heading}>
                        {control.label}
                      </label>
                      <div className="text-2xl font-bold" style={{color: colors.accent, ...fontStyles.heading}}>
                        {control.prefix || ""}{control.value}
                      </div>
                    </div>
                    <motion.input
                      type="range"
                      min={control.min}
                      max={control.max}
                      value={control.value}
                      onChange={(e) => control.setter(parseInt(e.target.value))}
                      className="w-full h-3 rounded-full appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, ${colors.accent} 0%, ${colors.accent} ${((control.value - control.min) / (control.max - control.min)) * 100}%, ${colors.gray200} ${((control.value - control.min) / (control.max - control.min)) * 100}%, ${colors.gray200} 100%)`,
                        outline: 'none'
                      }}
                      whileHover={{ scale: 1.01 }}
                    />
                    <p className="text-sm mt-2" style={{color: colors.gray600, ...fontStyles.body}}>
                      {control.helpText}
                    </p>
                  </div>
                ))}

                {/* Common Contract Types */}
                <div className="mt-8 p-4 rounded-2xl" style={{backgroundColor: colors.gray100}}>
                  <h4 className="text-sm font-semibold mb-3" style={{color: colors.gray700, ...fontStyles.heading}}>
                    Common contracts in {wasteData.industryData.name}:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {wasteData.industryData.commonContracts.map((contract, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 rounded-full text-xs"
                        style={{
                          backgroundColor: colors.accent,
                          color: colors.white,
                          ...fontStyles.medium
                        }}
                      >
                        {contract}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Waste Analysis Results */}
            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0, x: 30 }}
              animate={calculatorInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {/* Monthly Waste */}
              <motion.div 
                className="p-8 rounded-3xl border"
                style={{
                  borderColor: colors.accent,
                  background: `linear-gradient(135deg, ${colors.white} 0%, ${colors.accentLight} 100%)`,
                  boxShadow: '0 8px 32px rgba(30, 64, 175, 0.1)'
                }}
                whileHover={{ y: -4, transition: { type: "spring", stiffness: 300, damping: 25 } }}
              >
                <div className="flex items-center mb-6">
                  <Clock size={24} style={{color: colors.accent}} className="mr-3" />
                  <h3 className="text-xl font-semibold" style={fontStyles.heading}>Monthly Contract Chaos</h3>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-3xl font-bold mb-1" style={{color: colors.accent, ...fontStyles.heading}}>
                      ${wasteData.monthlyCost.toLocaleString()}
                    </div>
                    <div className="text-sm" style={{color: colors.gray600, ...fontStyles.body}}>wasted monthly</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold mb-1" style={{color: colors.gray900, ...fontStyles.heading}}>
                      {wasteData.monthlyHours}
                    </div>
                    <div className="text-sm" style={{color: colors.gray600, ...fontStyles.body}}>hours burned</div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t" style={{borderColor: `${colors.accent}30`}}>
                  <div className="text-sm" style={{color: colors.gray700, ...fontStyles.body}}>
                    Per contract: <strong>${wasteData.costPerContract.toLocaleString()}</strong> • <strong>{wasteData.hoursPerContract} hours</strong>
                  </div>
                </div>
              </motion.div>

              {/* Annual Waste - Main Impact */}
              <motion.div 
                className="p-8 rounded-3xl border"
                style={{
                  borderColor: colors.gray900,
                  background: `linear-gradient(135deg, ${colors.white} 0%, ${colors.gray100} 100%)`,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }}
                whileHover={{ y: -4, transition: { type: "spring", stiffness: 300, damping: 25 } }}
              >
                <div className="flex items-center mb-6">
                  <AlertTriangle size={24} style={{color: colors.gray900}} className="mr-3" />
                  <h3 className="text-xl font-semibold" style={fontStyles.heading}>Total Annual Waste</h3>
                </div>
                
                <div className="text-center mb-8">
                  <div className="text-5xl font-bold mb-2" style={{color: colors.gray900, ...fontStyles.heading}}>
                    ${wasteData.totalAnnualWaste.toLocaleString()}
                  </div>
                  <div className="text-lg" style={{color: colors.gray700, ...fontStyles.body}}>
                    thrown away every year
                  </div>
                </div>

                {/* Breakdown */}
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center p-3 rounded-xl" style={{backgroundColor: colors.gray100}}>
                    <span className="text-sm font-medium" style={{color: colors.gray700, ...fontStyles.medium}}>
                      Direct workflow costs
                    </span>
                    <span className="font-bold" style={{color: colors.gray900, ...fontStyles.heading}}>
                      ${wasteData.annualCost.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-xl" style={{backgroundColor: colors.gray100}}>
                    <span className="text-sm font-medium" style={{color: colors.gray700, ...fontStyles.medium}}>
                      Lost team productivity
                    </span>
                    <span className="font-bold" style={{color: colors.gray900, ...fontStyles.heading}}>
                      ${wasteData.lostProductivity.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-xl" style={{backgroundColor: colors.gray100}}>
                    <span className="text-sm font-medium" style={{color: colors.gray700, ...fontStyles.medium}}>
                      Contract value erosion (8.6%)
                    </span>
                    <span className="font-bold" style={{color: colors.gray900, ...fontStyles.heading}}>
                      ${wasteData.valueErosion.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* CTA Button */}
                <motion.button 
                  className="w-full px-8 py-4 rounded-2xl text-lg font-bold text-white relative overflow-hidden"
                  style={{
                    backgroundColor: colors.accent,
                    boxShadow: '0 8px 32px rgba(30, 64, 175, 0.3)',
                    ...fontStyles.heading
                  }}
                  onClick={() => window.open('https://cal.com/ranvirdeshmukh/book-a-demo-signpact', '_blank')}
                  whileHover={{ 
                    scale: 1.02,
                    boxShadow: '0 12px 40px rgba(30, 64, 175, 0.4)',
                    transition: { type: "spring", stiffness: 300, damping: 25 }
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.div
                    className="absolute inset-0 bg-white opacity-20"
                    variants={shimmerVariants}
                    initial="initial"
                    animate="animate"
                  />
                  <Target size={20} className="mr-2 inline" />
                  Get Custom Solution - Book Discovery Call
                </motion.button>
                <div className="text-sm mt-3 text-center" style={{color: colors.gray600, ...fontStyles.body}}>
                  Free consultation • Custom pricing • No obligation
                </div>
              </motion.div>

              {/* Industry Benchmark Context */}
              <motion.div 
                className="p-6 rounded-2xl border"
                style={{
                  borderColor: colors.gray300,
                  backgroundColor: colors.gray100
                }}
                whileHover={{ y: -2, transition: { type: "spring", stiffness: 300, damping: 25 } }}
              >
                <div className="flex items-center mb-4">
                  <AlertCircle size={20} style={{color: colors.gray600}} className="mr-2" />
                  <h4 className="text-lg font-semibold" style={{color: colors.gray900, ...fontStyles.heading}}>Industry Reality Check</h4>
                </div>
                <div className="text-sm space-y-2" style={{color: colors.gray700, ...fontStyles.body}}>
                  <div>• Companies lose <strong>9.2% of bottom line</strong> to poor contract management</div>
                  <div>• <strong>5% of revenue</strong> spent tracking agreements post-signature</div>
                  <div>• Average <strong>8.6% contract value erosion</strong> (Deloitte study)</div>
                  <div>• Your numbers are <strong>within industry norms</strong> for this chaos</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <ResponsiveFooter />
    </div>
  );
};

export default ContractWasteCalculator; 